import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { CaretRight, CaretDown, ClockCounterClockwise, CheckCircle, XCircle } from "@phosphor-icons/react";

/**
 * Recent Batches — collapsible history panel.
 * Props: { batchType: "po" | "invoice", refreshKey: number, openHref: (item) => string }
 */
export default function RecentBatches({ batchType, refreshKey = 0, openHref }) {
  const [batches, setBatches] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/batches", { params: { batch_type: batchType, limit: 25 } })
      .then((r) => setBatches(r.data))
      .finally(() => setLoading(false));
  }, [batchType, refreshKey]);

  if (loading) {
    return (
      <div className="jde-panel" style={{ marginTop: 12 }}>
        <div className="jde-panel-header">
          <span><ClockCounterClockwise size={13} weight="bold" style={{ display: "inline", marginRight: 6, marginBottom: -2 }} /> Recent Batches</span>
        </div>
        <div className="jde-panel-body muted" style={{ fontSize: 12 }}>Loading…</div>
      </div>
    );
  }

  if (batches.length === 0) return null;

  return (
    <div className="jde-panel" style={{ marginTop: 12 }} data-testid="recent-batches">
      <div className="jde-panel-header">
        <span>
          <ClockCounterClockwise size={13} weight="bold" style={{ display: "inline", marginRight: 6, marginBottom: -2 }} />
          Recent Batches
        </span>
        <span className="muted" style={{ fontSize: 11, fontWeight: 500 }}>Last {batches.length}</span>
      </div>
      <div>
        {batches.map((b) => {
          const open = !!expanded[b.id];
          return (
            <div key={b.id} data-testid={`batch-${b.id}`}>
              <div
                onClick={() => setExpanded((s) => ({ ...s, [b.id]: !s[b.id] }))}
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1.6fr 1fr 1fr 1fr",
                  gap: 12, alignItems: "center",
                  padding: "10px 14px",
                  borderTop: "1px solid var(--omh-divider)",
                  cursor: "pointer",
                  background: open ? "var(--omh-bg-subtle)" : undefined,
                }}
                data-testid={`batch-row-${b.id}`}
              >
                {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {fmtDateTime(b.created_at)}
                  </div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    by {b.user || "—"} · parallel {b.concurrency} ·
                    {b.duration_ms != null && ` ${(b.duration_ms / 1000).toFixed(1)}s`}
                  </div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                  <strong>{b.total}</strong> file{b.total === 1 ? "" : "s"}
                </span>
                <span className="success-text" style={{ fontSize: 12, fontWeight: 600 }}>
                  <CheckCircle size={12} weight="bold" style={{ display: "inline", marginBottom: -2, marginRight: 3 }} />
                  {b.succeeded} ok
                </span>
                <span style={{ fontSize: 12, fontWeight: 600, color: b.failed > 0 ? "var(--omh-alert)" : "var(--omh-text-muted)" }}>
                  <XCircle size={12} weight="bold" style={{ display: "inline", marginBottom: -2, marginRight: 3 }} />
                  {b.failed} failed
                </span>
              </div>
              {open && (
                <div style={{ borderTop: "1px solid var(--omh-divider)", background: "var(--omh-bg-subtle)" }}>
                  <table className="jde-grid" style={{ background: "transparent" }} data-testid={`batch-items-${b.id}`}>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th>Filename</th>
                        <th>Size</th>
                        <th>Status</th>
                        <th>Captured</th>
                        <th style={{ width: 80 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(b.items || []).map((it, i) => (
                        <tr key={i}>
                          <td className="muted">{i + 1}</td>
                          <td style={{ fontWeight: 600 }}>{it.filename}</td>
                          <td className="muted">{((it.size || 0) / 1024).toFixed(1)} KB</td>
                          <td>
                            {it.status === "done" && <span className="jde-badge shipped">Done</span>}
                            {it.status === "failed" && <span className="jde-badge alert">Failed</span>}
                            {it.status !== "done" && it.status !== "failed" && <span className="jde-badge closed">{it.status}</span>}
                          </td>
                          <td className="text-mono" style={{ fontSize: 11 }}>
                            {it.related_number || (it.error ? <span className="alert-text">{it.error}</span> : "—")}
                          </td>
                          <td>
                            {it.status === "done" && it.related_id && openHref && (
                              <Link to={openHref(it)} className="jde-btn jde-btn-sm">Open</Link>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
